import { MAP_TOGGLES, type Visibility } from '../visibility.js';
import { createDisclosure } from './disclosure.js';
import type { FeedHealth } from '../feedHealth.js';

const LAYERS_SVG =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
  'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
  '<polygon points="12 2 22 8.5 12 15 2 8.5 12 2"/>' +
  '<polyline points="2 15.5 12 22 22 15.5"/></svg>';

interface LayerGroup {
  title: string;
  ids: string[];
}

/** Toggle ids grouped into the panel's sections. Order defines display order. */
const GROUPS: LayerGroup[] = [
  // 'ais' (ships) removed for launch — see server index.ts.
  { title: 'Transit', ids: ['subway', 'subwaymap', 'buses', 'lirr', 'lirrmap', 'mnr', 'mnrmap', 'path', 'pathmap', 'ferry', 'ene'] },
  { title: 'Air', ids: ['flights'] },
  { title: 'Bikes', ids: ['citibike'] },
  { title: 'Safety', ids: ['c311', 'cams', 'linknyc'] },
  { title: 'City', ids: ['traffic'] },
  { title: 'Weather', ids: ['radar', 'floodnet', 'harbor'] },
  { title: 'Infrastructure', ids: ['power_outages', 'grid_load'] },
  { title: 'You', ids: ['location'] },
];

/** Display labels, derived from the single source of truth in visibility.ts. */
const LABELS: Record<string, string> = Object.fromEntries(MAP_TOGGLES.map((t) => [t.id, t.label]));

/** Build the Layers button + its popover/sheet panel of grouped toggles. */
export function createLayersPanel(
  parent: HTMLElement,
  vis: Visibility,
  health?: FeedHealth,
  isAvailable: (id: string) => boolean = () => true,
): { destroy(): void } {
  const disclosure = createDisclosure(parent, {
    ariaLabel: 'Layers',
    svg: LAYERS_SVG,
    panelClass: 'panel panel--layers',
  });
  const { panel } = disclosure;

  // Row label lives in its own span so the warning marker can be added and
  // removed without touching the label text.
  const syncWarn = (row: HTMLButtonElement, id: string): void => {
    const existing = row.querySelector('.toggle-warn');
    const state = health?.isDegraded(id) ? health.getDegraded().find((f) => f.layerId === id) : undefined;
    if (!state) {
      existing?.remove();
      return;
    }
    const marker = existing instanceof HTMLElement ? existing : document.createElement('span');
    marker.className = 'toggle-warn';
    marker.textContent = '⚠';
    marker.title =
      state.status === 'stale'
        ? `${state.label} — data delayed`
        : `${state.label} — live feed unavailable`;
    if (!existing) row.appendChild(marker);
  };

  const rows = new Map<string, HTMLButtonElement>();

  GROUPS.forEach((grp, i) => {
    const headingId = `layer-group-${i}`;
    const section = document.createElement('div');
    section.className = 'panel-section';
    section.setAttribute('role', 'group');
    section.setAttribute('aria-labelledby', headingId);

    const heading = document.createElement('div');
    heading.className = 'panel-heading';
    heading.id = headingId;
    heading.textContent = grp.title;
    section.appendChild(heading);

    for (const id of grp.ids) {
      if (!isAvailable(id)) continue;
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'panel-toggle';
      row.setAttribute('role', 'switch');
      row.setAttribute('aria-checked', String(vis.isVisible(id)));
      const label = document.createElement('span');
      label.textContent = LABELS[id] ?? id;
      row.appendChild(label);
      row.addEventListener('click', () => {
        vis.toggle(id);
      });
      rows.set(id, row);
      syncWarn(row, id);
      section.appendChild(row);
    }
    panel.appendChild(section);
  });

  const offVisibility = vis.onChange(() => {
    for (const [id, row] of rows) {
      row.setAttribute('aria-checked', String(vis.isVisible(id)));
    }
  });

  const offHealth = health?.onChange(() => {
    for (const [id, row] of rows) syncWarn(row, id);
  });
  return { destroy() { offVisibility(); offHealth?.(); disclosure.destroy(); } };
}
